// Relative imports (not `@/`) so bun can test this module.
import { getHexTileMapKey } from '../hexmap/model/keys';
import type { RuleSet } from '../rules/schema';
import { unitGlyph } from '../rules/ui';
import {
  type Building,
  buildingLevel,
  type EntityId,
  gatherRate,
  levelDef,
  levelStat,
  maxLevel,
  mintOrder,
  outsideTerritory,
  type SimContext,
  type SimState,
  storageCapacity,
  territoryOwners,
  toMilli,
  type Unit,
  workerSlots,
  workersInside,
} from '../sim';
import { cityName } from './cities';
import { type CityWorkersInput, cityWorkers, type PlaceRow } from './cityWorkers';
import { resourceIcon } from './resources';
import type { AppliedRow, TaskTone } from './unitInfo';
import { clockText, type UpgradeProgress } from './upgradeProgress';

/**
 * What the building info panel says about an own building (pure, no React):
 * its task line, the actual output of a resource building against its
 * maximum, the roster of its workers, what is applied to it and what it adds
 * to its city. Same four slots as the unit panel (header, task, stats,
 * applied and next); rival buildings never reach it.
 */

export interface BuildingTask {
  tone: TaskTone;
  /** "Working", "Idle: no workers", "Storing 2/5". */
  verb: string;
}

/**
 * The task line of a completed own building. A timed upgrade is NOT here: see
 * `upgradingTask`. `workers`: how many of its workers gather
 * now (`BuildingOutput.workers`) and how many are assigned to it at all.
 */
export function buildingTask(
  state: SimState,
  rules: RuleSet,
  b: Building,
  workers: { working: number; assigned: number; hauling?: number; full?: number }
): BuildingTask {
  const def = rules.buildings[b.type];
  if (b.buildProgress < 1) return { tone: 'work', verb: 'Under construction' };
  if (outsideTerritory(state, rules, b))
    return { tone: 'idle', verb: 'Idle: outside your territory' };
  if (def?.provides) {
    if (workers.working > 0) return { tone: 'work', verb: 'Working' };
    let verb = 'Idle: no workers';
    if (workers.assigned > 0) {
      if ((workers.hauling ?? 0) > 0) verb = 'Idle: workers hauling';
      else if ((workers.full ?? 0) >= workers.assigned) verb = 'Idle: full loads';
      else verb = 'Idle: workers on the way';
    }
    return { tone: 'idle', verb };
  }
  const capacity = storageCapacity(rules, b);
  if (capacity > 0) {
    const stored = Object.values(state.units).filter((u) => u.inside === b.id).length;
    return { tone: stored > 0 ? 'work' : 'idle', verb: `Storing ${stored}/${capacity}` };
  }
  if (b.watch) return { tone: 'work', verb: 'Watching' };
  return { tone: 'work', verb: 'Active' };
}

/**
 * The task line while an own building upgrades (`upgradeProgress`, owner
 * only): "Upgrading to level 2 · 0:12 of 0:30 · old stats till done", or the
 * paused state when it was hit recently. The old level keeps working until
 * done, so this replaces the verb but not the output figures.
 */
export function upgradingTask(rules: RuleSet, b: Building, up: UpgradeProgress): BuildingTask {
  const def = rules.buildings[b.type];
  const next = def && levelDef(def, up.level);
  const total = next?.upgradeTime ?? 0;
  const head = `Upgrading to level ${up.level}`;
  if (up.paused) return { tone: 'idle', verb: `${head} · paused: under attack` };
  const elapsed = Math.max(0, total - up.secondsLeft);
  return {
    tone: 'work',
    verb: `${head} · ${clockText(elapsed)} of ${clockText(total)} · old stats till done`,
  };
}

/** The city (own, completed or not) whose territory holds building `b`. */
export function cityOf(state: SimState, rules: RuleSet, b: Building): Building | undefined {
  const id = territoryOwners(state, rules).get(getHexTileMapKey(b.pos));
  const city = id === undefined ? undefined : state.buildings[id];
  return city?.owner === b.owner ? city : undefined;
}

export interface BuildingOutput {
  resource: string;
  /** Units per second the workers inside gather now. */
  now: number;
  /** The level's `provides.rate` multiplier on the wild rate. */
  rateMultiplier: number;
  workers: number;
  slots: number;
  /** The city's `gatherBonus` (0.1 = +10%) and the city's level title ("Town"). */
  cityBonus: number;
  cityTitle: string | null;
  /** Units per second with every slot filled by a plain worker. */
  max: number;
}

/**
 * A resource building's output now and at full slots. `now` is the sim's own
 * `gatherRate` summed over the workers that are working it (those inside with
 * room in their load, up to its slots), so a labourer's tools and a full
 * load are counted as the sim counts them; `max` assumes plain workers.
 */
export function buildingOutput(
  state: SimState,
  ctx: SimContext,
  b: Building
): BuildingOutput | null {
  const { rules } = ctx;
  const def = rules.buildings[b.type];
  if (!def?.provides || b.buildProgress < 1) return null;
  const slots = workerSlots(rules, b);
  const outside = outsideTerritory(state, rules, b);
  const rateMultiplier = levelStat(def, buildingLevel(b), 'rate') ?? 1;
  const city = cityOf(state, rules, b);
  const cityDef = city && rules.buildings[city.type];
  const cityBonus =
    city && cityDef ? (levelStat(cityDef, buildingLevel(city), 'gatherBonus') ?? 0) : 0;
  const working = outside ? [] : workersInside(state, rules, b).slice(0, slots);
  const now = working.reduce((sum, u) => sum + gatherRate(state, ctx, u), 0);
  const max = outside ? 0 : slots * rules.pacing.baseGatherRate * rateMultiplier * (1 + cityBonus);
  return {
    resource: def.provides.resource,
    now,
    rateMultiplier,
    workers: working.length,
    slots,
    cityBonus,
    cityTitle:
      city && cityDef && cityBonus > 0
        ? (cityDef.levels?.[buildingLevel(city) - 2]?.title ?? cityDef.name)
        : null,
    max,
  };
}

/** One worker chip of the roster. */
export interface RosterWorker {
  id: EntityId;
  /** The unit's mint number, the "3" in "Wk 3". */
  number: number;
  glyph: string;
  /** Its home city as the viewer names it ("City 2"); null when it has none. */
  home: string | null;
  /** Home is another city than the building's. */
  foreignHome: boolean;
  /** What it is doing: "gathering", "waiting for a slot", "full load, waiting", "hauling a load", "walking here". */
  doing: string;
}

/**
 * Everyone assigned to a farm, forestry or mine, read-only: the units inside,
 * the automated workers hauling for it (as `assignedWorkers` counts) and the
 * pinned ones walking to it, in mint order. Own building only, so no fog
 * question. "gathering" is exactly the sim's `workersInside(...)` first
 * `workerSlots`.
 */
export function buildingRoster(
  state: SimState,
  ctx: SimContext,
  b: Building,
  cityNumbers: ReadonlyMap<string, number>
): RosterWorker[] {
  const { rules } = ctx;
  const tiles = new Set([getHexTileMapKey(b.pos), ...(b.footprint ?? []).map(getHexTileMapKey)]);
  const homeOf = (u: Unit) => (u.homeCityId ? state.buildings[u.homeCityId] : undefined);
  const city = cityOf(state, rules, b);
  const working = new Set(
    outsideTerritory(state, rules, b)
      ? []
      : workersInside(state, rules, b)
          .slice(0, workerSlots(rules, b))
          .map((u) => u.id)
  );
  const out: RosterWorker[] = [];
  for (const u of Object.values(state.units)) {
    if (u.owner !== b.owner) continue;
    let doing: string | null = null;
    if (u.inside === b.id) {
      const capacity = rules.units[u.type]?.carryCapacity;
      const full = u.carry && capacity !== undefined && u.carry.amount >= toMilli(capacity);
      doing = working.has(u.id) ? 'gathering' : full ? 'full load, waiting' : 'waiting for a slot';
    } else if (!u.inside && u.order?.kind === 'gather' && u.order.pin && !u.order.haul) {
      // A pinned worker (assign deletes `auto`) on its way to the building.
      if (tiles.has(getHexTileMapKey(u.order.target))) doing = 'walking here';
    } else if (u.auto && !u.inside) {
      const work = u.auto.phase === 'haul' ? u.auto.work : u.auto.target;
      if (work && tiles.has(getHexTileMapKey(work))) {
        doing = u.auto.phase === 'haul' ? 'hauling a load' : 'walking here';
      }
    }
    if (!doing) continue;
    const home = homeOf(u);
    out.push({
      id: u.id,
      number: mintOrder(u.id),
      glyph: unitGlyph(rules, u.type),
      home: home ? cityName(home, b.owner, cityNumbers) : null,
      foreignHome: !!home && !!city && home.id !== city.id,
      doing,
    });
  }
  return out.sort((a, c) => a.number - c.number);
}

/**
 * What is applied to a building: each level it was raised to (rate, slots),
 * the city's gather bonus on a resource building. Reads the def's levels, so
 * nothing is hard-coded.
 */
export function buildingApplied(
  state: SimState,
  rules: RuleSet,
  b: Building,
  cityNumbers: ReadonlyMap<string, number>
): AppliedRow[] {
  const def = rules.buildings[b.type];
  if (!def) return [];
  const rows: AppliedRow[] = [];
  const level = buildingLevel(b);
  const num = (v: number) => String(Number(v.toFixed(2)));
  for (let l = 2; l <= Math.min(level, maxLevel(def)); l++) {
    const step = def.levels?.[l - 2];
    if (!step) continue;
    const prev = (stat: 'rate' | 'workerSlots') => levelStat(def, l - 1, stat);
    if (step.rate !== undefined) {
      rows.push({
        src: '⬆',
        label: `Level ${l}`,
        effect: `${num(prev('rate') ?? 1)} → ${num(step.rate)}× rate`,
      });
    }
    if (step.workerSlots !== undefined) {
      rows.push({
        src: '⬆',
        label: `Level ${l}`,
        effect: `${prev('workerSlots') ?? 1} → ${step.workerSlots} slots`,
      });
    }
  }
  const city = cityOf(state, rules, b);
  const cityDef = city && rules.buildings[city.type];
  const bonus = city && cityDef ? (levelStat(cityDef, buildingLevel(city), 'gatherBonus') ?? 0) : 0;
  if (def.provides && city && bonus > 0) {
    rows.push({
      src: '⌂',
      label: cityName(city, b.owner, cityNumbers),
      effect: `+${Number((bonus * 100).toFixed(1))}% gather`,
    });
  }
  return rows;
}

export interface CityEffect {
  city: string;
  resource: string;
  total: number;
  /** Every such building of the city, this one included, with its output now. */
  parts: { id: EntityId; name: string; now: number; self: boolean }[];
}

/**
 * "Food +0.81/s from 2 farms": what this resource building adds to its city,
 * next to the city's other buildings giving the same resource. Null for a
 * building that provides nothing or stands outside every own city.
 */
export function cityEffect(
  state: SimState,
  ctx: SimContext,
  b: Building,
  cityNumbers: ReadonlyMap<string, number>
): CityEffect | null {
  const { rules } = ctx;
  const resource = rules.buildings[b.type]?.provides?.resource;
  const city = cityOf(state, rules, b);
  if (!resource || !city) return null;
  const owners = territoryOwners(state, rules);
  const parts = Object.values(state.buildings)
    .filter(
      (o) =>
        o.owner === b.owner &&
        o.buildProgress >= 1 &&
        rules.buildings[o.type]?.provides?.resource === resource &&
        owners.get(getHexTileMapKey(o.pos)) === city.id
    )
    .sort((a, c) => mintOrder(a.id) - mintOrder(c.id))
    .map((o, i) => ({
      id: o.id,
      name: `${(rules.buildings[o.type]?.name ?? o.type).slice(0, 1)}${i + 1}`,
      now: buildingOutput(state, ctx, o)?.now ?? 0,
      self: o.id === b.id,
    }));
  return {
    city: cityName(city, b.owner, cityNumbers),
    resource,
    total: parts.reduce((s, p) => s + p.now, 0),
    parts,
  };
}

/** "0.37🌾/s" */
export function rateText(resource: string, perSecond: number): string {
  return `${Number(perSecond.toFixed(2))}${resourceIcon(resource)}/s`;
}

/**
 * The Workers tab's row for building `buildingId` (`cityWorkers`, #102): its
 * worker chips (pinned / auto / visiting) and what "+ add idle worker" would
 * send or why it can't. Null when the building has no row (not a work place
 * of that city).
 */
export function workPlace(input: CityWorkersInput, buildingId: EntityId): PlaceRow | null {
  for (const task of cityWorkers(input).tasks) {
    const row = task.places.find((p) => p.key === buildingId);
    if (row) return row;
  }
  return null;
}
